import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { PremiumDialog } from '@root/shared/ui/premium-dialog'

import { reatomUpdateLogoDrawer } from '../../atoms'
import { PremiumImageDrawerPreview } from '../common/update-logo-drawer-preview'
import { UpdateLogoProgress } from '../common/update-logo-progress'

import type { CurrencyModel } from '@root/entities/currency'
import type { PairModel } from '@root/entities/pair'
import type { PremiumMetaModel } from '@root/entities/premium'
import type { DialogControlProps } from '@root/shared/ui-kit'

type Props = {
  currency: CurrencyModel
  premiumMeta: PremiumMetaModel
  pair: PairModel | undefined
} & DialogControlProps

export const UploadPremiumLogoDialog: React.FC<Props> = ({
  currency,
  premiumMeta,
  pair,
  open,
  onOpenChange,
}) => {
  const imageInputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomUpdateLogoDrawer({
        premiumMeta,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const [requestState] = useAtom(model.updateRequestState)
  const [preview] = useAtom(model.previewAtom)
  const [image] = useAtom(model.imageAtom)
  const [currentIcon] = useAtom(currency.icon)

  const uploadLogo = useAction(model.updateLogoAction)
  const setImage = useAction(model.setImageAction)
  const clearImage = useAction(model.clearImageAction)
  const reset = useAction(model.resetAction)

  const handleUploadImage = () =>
    imageInputRef.current?.dispatchEvent(
      new MouseEvent('click', { bubbles: true }),
    )

  const clearFileInput = () => {
    if (imageInputRef.current) {
      imageInputRef.current.value = ''
    }
  }
  const handleReset = () => {
    reset()
    clearFileInput()
  }
  const handleClose = () => {
    handleReset()
    onOpenChange?.(false)
  }
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleReset()
    }
    onOpenChange?.(nextOpen)
  }
  const handleRevertImage = () => {
    clearImage()
    clearFileInput()
  }

  return (
    <Root
      open={open}
      onOpenChange={handleOpenChange}
      title={t('premium.update-logo.title')}
      describedBy={t('premium.update-logo.title')}
      onClose={handleClose}
    >
      <UpdateLogoProgress
        requestState={requestState}
        onClose={handleClose}
        onReset={handleReset}
      />
      <PremiumImageDrawerPreview
        onUploadClick={handleUploadImage}
        onRevertClick={preview ? handleRevertImage : undefined}
        imagePreview={preview || currentIcon}
        pair={pair}
      />
      <Input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) {
            setImage(file)
          }
        }}
      />
      <Action>
        <ButtonBack
          variant="secondary"
          radius="rounded"
          size="m"
          onClick={handleClose}
        >
          {t('common.back')}
        </ButtonBack>
        <Button
          variant="solid"
          color="primary"
          size="m"
          radius="rounded"
          disabled={!image}
          onClick={uploadLogo}
        >
          {t('common.process')}
        </Button>
      </Action>
    </Root>
  )
}

const Root = styled(PremiumDialog)`
  display: grid;
  text-align: center;
`

const Action = styled.footer`
  display: grid;
  grid-template-columns: 1fr 1fr;

  gap: 10px;
  padding: 42px 12px 34px;
`

const ButtonBack = styled(Button)`
  color: var(--text-11);
`

const Input = styled.input`
  display: none;
`
